import { useContext, useEffect, useMemo, useState } from "react";
import { Context } from "../../../context/Context";
import { useRestaurantAccess } from "../../../access/hooks/useRestaurantAccess";
import { Button, Input, Radio, Rocket, Select } from "../../ui";
import { fetchCampaignsAPI } from "../../../utils/api/operations";
import { createCampaignAPI } from "../../../utils/api/actions";
import { DELIVERY_APPS, integrationOptions, normalizeIntegrationKey } from "../../../utils/integrations";
import { MAX_CAMPAIGN_NAME, getCampaignTypeOptions, toCatalogItems } from "../operationUtils";
import { getCampaignText } from "./campaignActionsText";
import CampaignModal from "./CampaignModal";
import CampaignPicker from "./CampaignPicker";
import MultiSelectList from "./MultiSelectList";
import { describeError } from "./useStoreCampaigns";

const MODES = { CATALOG: "catalog", MANUAL: "manual" };

// Activate Campaign: choose an existing campaign (visual picker) or add one
// by hand, as a new request or as a campaign that is already active.
export default function ActivateCampaignModal({ stores, onClose, onDone }) {
  const { canForAll } = useRestaurantAccess(stores);
  const { lang } = useContext(Context);
  const text = getCampaignText(lang);

  const [mode, setMode] = useState(MODES.CATALOG);
  const [storeIds, setStoreIds] = useState(stores.length === 1 ? [stores[0].id] : []);
  const [appName, setAppName] = useState("");
  const [catalog, setCatalog] = useState({ data: null, loading: false, error: "" });
  const [catalogTick, setCatalogTick] = useState(0);
  const [selected, setSelected] = useState(null);
  const [name, setName] = useState("");
  const [campaignType, setCampaignType] = useState("");
  const [details, setDetails] = useState("");
  const [initialState, setInitialState] = useState("request");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // A chosen campaign can only be requested for restaurants that have its app,
  // and a catalog reserved for one restaurant only for that restaurant (the
  // server also rejects a wrong reservation or a campaign of another app).
  const reservedFor = mode === MODES.CATALOG ? catalog.data?.storeId : null;
  const storeOptions = useMemo(
    () =>
      stores
        .filter((store) => mode !== MODES.CATALOG || !appName || (store.apps || []).some((item) => normalizeIntegrationKey(item.app) === appName))
        .filter((store) => !reservedFor || store.id === reservedFor)
        .map((store) => ({ id: store.id, label: store.storeName, hint: "#" + store.id })),
    [stores, mode, appName, reservedFor],
  );

  // The selection never outlives the restaurants on offer; a lone restaurant is preselected.
  useEffect(() => {
    setStoreIds((current) => {
      if (stores.length === 1 && storeOptions.length === 1) return current.length === 1 ? current : [storeOptions[0].id];
      const next = current.filter((id) => storeOptions.some((option) => option.id === id));
      return next.length === current.length ? current : next;
    });
  }, [stores, storeOptions]);

  // The campaign list belongs to the chosen app.
  useEffect(() => {
    if (mode !== MODES.CATALOG || !appName) return undefined;
    let active = true;
    const token = localStorage.getItem("auth-token");
    setCatalog({ data: null, loading: true, error: "" });
    setSelected(null);
    fetchCampaignsAPI(token, appName)
      .then((data) => {
        if (!active) return;
        setCatalog(data ? { data, loading: false, error: "" } : { data: null, loading: false, error: text.loadError });
      })
      .catch(() => active && setCatalog({ data: null, loading: false, error: text.loadError }));
    return () => {
      active = false;
    };
  }, [mode, appName, catalogTick, text.loadError]);

  const catalogItems = useMemo(() => toCatalogItems(catalog.data, lang), [catalog.data, lang]);
  const appOptions = useMemo(() => integrationOptions(DELIVERY_APPS, lang), [lang]);
  const typeOptions = useMemo(() => getCampaignTypeOptions(lang), [lang]);

  const isCatalog = mode === MODES.CATALOG;
  const datesInvalid = Boolean(startDate && endDate && endDate < startDate);
  const ready = isCatalog
    ? Boolean(selected)
    : Boolean(name.trim() && campaignType && details.trim());
  // Only a name typed by hand is limited; catalog names are used as they are.
  const nameTooLong = !isCatalog && name.trim().length > MAX_CAMPAIGN_NAME;
  const submitDisabled = submitting || !storeIds.length || !appName || !ready || datesInvalid || nameTooLong;
  const activeNow = !isCatalog && initialState === "active";

  const handleSubmit = async () => {
    if (submitDisabled) return;
    // handler-level authorization: every selected restaurant must be in scope
    if (!canForAll("campaign.request.create", storeIds)) return;
    const payload = {
      storeIds,
      appName,
      source: mode,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      ...(isCatalog
        ? { campaignType: selected.type, catalogEntryId: selected.id, campaignDetails: details.trim() || undefined, initialState: "request" }
        : { campaignType, campaignName: name.trim(), campaignDetails: details.trim(), initialState }),
    };
    setSubmitting(true);
    setSubmitError("");
    try {
      await createCampaignAPI(localStorage.getItem("auth-token"), payload);
      onDone();
      onClose();
    } catch (error) {
      setSubmitError(describeError(error, text));
      setSubmitting(false);
    }
  };

  return (
    <CampaignModal
      title={text.activateTitle}
      icon={<Rocket size={20} aria-hidden="true" />}
      onClose={onClose}
      busy={submitting}
      closeLabel={text.close}
      footer={
        <>
          <Button variant="primary" appearance="filled" disabled={submitDisabled} loading={submitting} onClick={handleSubmit}>
            {activeNow ? text.submitActive : text.submitRequest}
          </Button>
          <Button variant="neutral" appearance="soft" disabled={submitting} onClick={onClose}>
            {text.close}
          </Button>
        </>
      }
    >
      <div className="ca-tabs" role="group">
        {[
          [MODES.CATALOG, text.fromCatalog],
          [MODES.MANUAL, text.manual],
        ].map(([key, label]) => (
          <Button
            key={key}
            size="sm"
            variant="primary"
            appearance={mode === key ? "filled" : "soft"}
            aria-pressed={mode === key}
            disabled={submitting}
            onClick={() => setMode(key)}
          >
            {label}
          </Button>
        ))}
      </div>

      <MultiSelectList
        label={text.restaurants}
        items={storeOptions}
        selectedIds={storeIds}
        onChange={setStoreIds}
        disabled={submitting}
        emptyText={isCatalog && appName ? text.noEligibleActivate : text.chooseStoreFirst}
      />
      <div className="ca-grid-2">
        <Select
          label={text.app}
          placeholder={text.selectApp}
          options={appOptions}
          value={appName}
          disabled={submitting}
          onChange={(event) => setAppName(event.target.value)}
        />
      </div>

      {isCatalog ? (
        !appName ? (
          <p className="ca-picker-state">{text.chooseAppFirst}</p>
        ) : (
          <CampaignPicker
            items={catalogItems}
            loading={catalog.loading}
            error={catalog.error}
            onRetry={() => setCatalogTick((value) => value + 1)}
            emptyText={text.noCatalog}
            selectedId={selected?.id}
            onSelect={setSelected}
          />
        )
      ) : (
        <>
          <Input
            label={text.campaignName}
            required
            value={name}
            error={nameTooLong ? text.nameTooLong(MAX_CAMPAIGN_NAME, name.trim().length) : undefined}
            onChange={(event) => setName(event.target.value)}
          />
          <Select
            label={text.campaignType}
            options={typeOptions}
            value={campaignType}
            onChange={(event) => setCampaignType(event.target.value)}
          />
          <Input
            textarea
            required
            label={text.campaignDetails}
            value={details}
            maxLength={4000}
            onChange={(event) => setDetails(event.target.value)}
          />
          <fieldset className="ca-state">
            <legend>{text.initialState}</legend>
            <Radio
              name="ca-initial-state"
              label={text.stateRequest}
              value="request"
              checked={initialState === "request"}
              onChange={() => setInitialState("request")}
            />
            <Radio
              name="ca-initial-state"
              label={text.stateActive}
              value="active"
              checked={initialState === "active"}
              onChange={() => setInitialState("active")}
            />
            <p className="ca-hint">{initialState === "active" ? text.stateActiveHint : text.stateRequestHint}</p>
          </fieldset>
        </>
      )}

      {isCatalog && selected && (
        <Input
          textarea
          label={text.notes}
          value={details}
          maxLength={4000}
          onChange={(event) => setDetails(event.target.value)}
        />
      )}

      <div className="ca-grid-2">
        <Input
          type="date"
          label={text.startDate}
          value={startDate}
          onChange={(event) => setStartDate(event.target.value)}
        />
        <Input
          type="date"
          label={text.endDate}
          value={endDate}
          min={startDate || undefined}
          error={datesInvalid || undefined}
          onChange={(event) => setEndDate(event.target.value)}
        />
      </div>
      <p className="ca-hint">{text.datesHint}</p>

      {submitError && (
        <p className="ca-error" role="alert">
          {submitError}
        </p>
      )}
    </CampaignModal>
  );
}
